/**
 * Razorpay Checkout helpers for the billing surface — a single place that owns
 * the global `Razorpay` typing + script loader, used by both the WhatsApp
 * top-up (one-time `order_id`) and self-serve subscription (`subscription_id`)
 * flows. Keeping one declaration avoids conflicting `Window.Razorpay` types.
 */
import * as React from "react";
import { toast } from "@flint/ui";
import { PLAN_BY_CODE, type PlanCode } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.ts";

interface RazorpayCheckout {
  open: () => void;
}
interface RazorpayBaseOptions {
  key: string;
  name: string;
  description: string;
  handler: () => void;
  modal?: { ondismiss?: () => void };
}
/** One-time order checkout vs. subscription mandate checkout. */
export type RazorpayOptions =
  | (RazorpayBaseOptions & { order_id: string; amount: number; currency: string })
  | (RazorpayBaseOptions & { subscription_id: string });

declare global {
  interface Window {
    Razorpay?: new (opts: RazorpayOptions) => RazorpayCheckout;
  }
}

const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

export function loadRazorpay(): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = RZP_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/** Load the SDK if needed and open Checkout. Returns false if the SDK won't load. */
export async function openCheckout(opts: RazorpayOptions): Promise<boolean> {
  const ready = await loadRazorpay();
  if (!ready || !window.Razorpay) return false;
  new window.Razorpay(opts).open();
  return true;
}

/**
 * Self-serve activation: create a Razorpay Subscription for `toPlan`, open the
 * mandate Checkout, and refresh the billing read-model after the customer
 * authorises (the `subscription.activated` webhook flips the status server-side,
 * so we just re-fetch — never trust the client handler for the transition).
 */
export function useSubscriptionCheckout() {
  const utils = trpc.useUtils();
  const create = trpc.billing.createSubscription.useMutation();
  const [pendingPlan, setPendingPlan] = React.useState<PlanCode | null>(null);

  async function activate(toPlan: PlanCode) {
    const name = PLAN_BY_CODE[toPlan]?.name ?? toPlan;
    setPendingPlan(toPlan);
    try {
      const order = await create.mutateAsync({ toPlan });
      const opened = await openCheckout({
        key: order.razorpayKeyId,
        subscription_id: order.subscriptionId,
        name: "Flint",
        description: `${name} plan — monthly`,
        handler: () => {
          toast.success("Payment authorised", {
            description: "Activating your plan — this takes a few seconds.",
          });
          // Poll until the webhook (or our own Razorpay query) confirms
          // activation. Exponential backoff: 1s → 2s → 4s → 8s → 10s cap,
          // ~55 s total window. @see ISS-006.
          void pollForActivation(utils);
        },
      });
      if (!opened) {
        toast.error("Couldn't open checkout", {
          description: "Please retry, or talk to us to activate.",
        });
      }
    } catch (err) {
      toast.error("Couldn't start activation", {
        description: err instanceof Error ? err.message : "Please talk to us to activate.",
      });
    } finally {
      setPendingPlan(null);
    }
  }

  return { activate, pendingPlan };
}

/**
 * Exponential-backoff polling after Checkout closes — asks the server whether
 * the subscription activated (webhook landed, or the server queries Razorpay
 * directly). 8 attempts, 1 s → 2 s → 4 s → 8 s → 10 s cap ≈ 55 s total
 * window. On exhaustion, shows a reassurance toast (payment is safe). @see ISS-006.
 */
async function pollForActivation(utils: ReturnType<typeof trpc.useUtils>) {
  const MAX_ATTEMPTS = 8;
  const MAX_DELAY = 10_000;
  let delay = 1000;

  for (let i = 0; i < MAX_ATTEMPTS; i++) {
    await new Promise<void>((r) => setTimeout(r, delay));
    try {
      const result = await utils.billing.checkActivation.fetch({});
      if (result.activated) {
        void utils.billing.overview.invalidate();
        void utils.identity.me.invalidate();
        return;
      }
    } catch {
      // Transient network error — keep polling; the next attempt may succeed.
    }
    delay = Math.min(delay * 2, MAX_DELAY);
  }

  // All attempts exhausted — reassure the customer their payment is safe.
  toast.error("Activation is taking longer than expected", {
    description:
      "Your payment was received. If your plan doesn't update in a few minutes, please refresh or contact support.",
    duration: 15_000,
  });
  void utils.billing.overview.invalidate();
  void utils.identity.me.invalidate();
}
